<template>
    <div class="st-card">
        <div class="st-card-header">
            <div class="st-card-icon">
                <i class="fal fa-cog"></i>
            </div>
            <div>
                <h2 class="st-card-title">{{ $t('companies.settings.name') }}</h2>
                <p class="st-card-desc">{{ $t('companies.settings.contact.title') }}</p>
            </div>
        </div>

        <div class="st-card-body">
            <!-- Email admin -->
            <div class="st-field">
                <label class="st-label" for="adminEmail">
                    {{ $t('companies.settings.contact.mail_admin') }} *
                </label>
                <input
                    id="adminEmail"
                    v-model="company.mail_admin"
                    required
                    class="st-input"
                    type="email"
                />
                <p v-if="isSettingsSend && nameMessage" class="st-error">{{ nameMessage }}</p>
            </div>

            <!-- Logo -->
            <div class="st-field">
                <label class="st-label">Logo</label>
                <div class="logo-grid">
                    <!-- No logo -->
                    <div
                        class="logo-card"
                        :class="{ 'is-selected': company.logo_settings === LOGO_SETTINGS.NO_LOGO }"
                        @click="company.logo_settings = LOGO_SETTINGS.NO_LOGO"
                    >
                        <div class="logo-card-preview logo-card-preview--empty">
                            <i class="fal fa-ban"></i>
                        </div>
                        <div class="logo-card-info">
                            <div class="logo-card-radio" :class="{ 'is-checked': company.logo_settings === LOGO_SETTINGS.NO_LOGO }">
                                <i v-if="company.logo_settings === LOGO_SETTINGS.NO_LOGO" class="fal fa-check" style="color: #fff !important; font-size: 0.55rem;"></i>
                            </div>
                            <span class="logo-card-label">{{ $t('companies.settings.logo_config.no_logo') }}</span>
                        </div>
                    </div>

                    <!-- BMV logo -->
                    <div
                        class="logo-card"
                        :class="{ 'is-selected': company.logo_settings === LOGO_SETTINGS.DEFAULT_LOGO }"
                        @click="company.logo_settings = LOGO_SETTINGS.DEFAULT_LOGO"
                    >
                        <div class="logo-card-preview">
                            <img src="/img/logo-1.png" alt="Bouge Ma Ville" />
                        </div>
                        <div class="logo-card-info">
                            <div class="logo-card-radio" :class="{ 'is-checked': company.logo_settings === LOGO_SETTINGS.DEFAULT_LOGO }">
                                <i v-if="company.logo_settings === LOGO_SETTINGS.DEFAULT_LOGO" class="fal fa-check" style="color: #fff !important; font-size: 0.55rem;"></i>
                            </div>
                            <span class="logo-card-label">{{ $t('companies.settings.logo_config.bmv_logo') }}</span>
                        </div>
                    </div>

                    <!-- Custom logo -->
                    <div
                        class="logo-card"
                        :class="{ 'is-selected': company.logo_settings === LOGO_SETTINGS.CUSTOM_LOGO }"
                        @click="company.logo_settings = LOGO_SETTINGS.CUSTOM_LOGO"
                    >
                        <div class="logo-card-preview logo-card-preview--upload">
                            <i class="fal fa-cloud-upload"></i>
                        </div>
                        <div class="logo-card-info">
                            <div class="logo-card-radio" :class="{ 'is-checked': company.logo_settings === LOGO_SETTINGS.CUSTOM_LOGO }">
                                <i v-if="company.logo_settings === LOGO_SETTINGS.CUSTOM_LOGO" class="fal fa-check" style="color: #fff !important; font-size: 0.55rem;"></i>
                            </div>
                            <span class="logo-card-label">{{ $t('companies.settings.logo_config.custom_logo') }}</span>
                        </div>
                    </div>
                </div>

                <BmvFileUpload
                    v-if="company.logo_settings === LOGO_SETTINGS.CUSTOM_LOGO"
                    class="mt-3"
                    :accepted-extensions="['png', 'jpg', 'jpeg']"
                    :value="logoAttribute.value || null"
                    @input="addCompanyLogo"
                />
            </div>
        </div>

        <div class="st-card-footer">
            <button class="st-btn st-btn--primary" @click.prevent="save">
                <i class="fal fa-save" style="color: #fff !important;"></i>
                {{ $t('companies.settings.change_settings') }}
            </button>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import BmvFileUpload from '@/components/UI/BmvFileUpload'
import { mapActions, mapState } from 'vuex'
import LOGO_SETTINGS from '@/helpers/logoSettings.js'

export default {
    name: 'SettingsContact',
    components: { BmvFileUpload },
    data() {
        return {
            LOGO_SETTINGS,
            isSettingsSend: false,
            nameMessage: '',
            logoAttribute: {
                config: {
                    'accepted-ext': ['png', 'jpg', 'jpeg', 'PNG', 'JPG', 'JPEG'],
                    max: 1,
                    min: 0,
                    type: 'pictures',
                    value: null
                }
            }
        }
    },
    computed: {
        ...mapState('company', {
            company: (state) => state.companySettings
        })
    },
    methods: {
        ...mapActions('company', ['sendCompanyAdminEmailAndLogo', 'getCompanySettings']),
        addCompanyLogo(value) {
            this.company.settings_logo = value
        },
        save() {
            this.isSettingsSend = true
            this.company.settings_logo =
                this.company.logo_settings == LOGO_SETTINGS.CUSTOM_LOGO
                    ? this.company.settings_logo
                    : null

            this.sendCompanyAdminEmailAndLogo()
                .then(() => {
                    return this.getCompanySettings()
                })
                .then(() => {
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('companies.settings.settings_modified')
                    })
                })
                .catch((error) => {
                    this.$store.dispatch('dispatchError', error)
                })
        },
        initLogoValue() {
            this.logoAttribute.value = this.company.settings_logo || null
        }
    },
    watch: {
        'company.settings_logo': {
            immediate: true,
            handler() {
                this.initLogoValue()
            }
        }
    }
}
</script>

<style scoped>
.logo-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.logo-card {
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s;
    background: #fff;
}

.logo-card:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.logo-card.is-selected {
    border-color: #0891B2;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.1);
}

.logo-card-preview {
    height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8fafc;
    border-bottom: 1px solid #f1f5f9;
}

.logo-card-preview img {
    max-height: 50px;
    max-width: 80%;
    object-fit: contain;
}

.logo-card-preview--empty {
    color: #cbd5e1;
    font-size: 1.5rem;
}

.logo-card-preview--upload {
    color: #0891B2;
    font-size: 1.5rem;
}

.logo-card.is-selected .logo-card-preview--upload {
    background: rgba(8, 145, 178, 0.04);
}

.logo-card-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
}

.logo-card-radio {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
}

.logo-card-radio.is-checked {
    background: #0891B2;
    border-color: #0891B2;
}

.logo-card-label {
    font-size: 0.78rem;
    font-weight: 600;
    color: #334155;
}

@media (max-width: 640px) {
    .logo-grid {
        grid-template-columns: 1fr;
    }
}
</style>
